import React from 'react';
import Skeleton from '../ui/Skeleton';
import { useNavigate, useLocation } from 'react-router-dom';
import './TrendingPanel.css';
import { useSelector } from 'react-redux';
import { getIssueLabel, getSolutionTypeValue, getSolutionTypeMeta } from '../../pages/home/Projects/utils/viewHelpers';
import { useFeedRefresh } from '../../features/feed';

export default function TrendingPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setPriorityPost } = useFeedRefresh();
  const posts = useSelector(state => state.trending.posts || []);
  const projects = useSelector(state => state.trending.projects || []);
  const ready = useSelector(state => state.trending.ready);
  const isLoading = !ready && posts.length === 0 && projects.length === 0;
  const isError = false;
  const error = null;

  // When clicking a trending post, set it as priority and navigate/scroll
  const openPost = (id) => {
    if (location.pathname === '/home') {
      setPriorityPost(id); // Volatile local state highlight & scroll
    } else {
      setPriorityPost(id); // Fallback
      navigate(`/home?post=${encodeURIComponent(id)}`);
    }
  };
  const openProject = (id) => navigate(`/projects/${id}`);



  const truncate = (s, n = 90) => {
    if (!s) return '';
    const str = String(s);
    return str.length > n ? str.slice(0, n) + '…' : str;
  };

  const renderCard = (item, type) => {
    const isPost = type === 'post';
    const open = () => (isPost ? openPost(item.id) : openProject(item.id));
    const title = truncate(isPost ? (item.title || item.content || 'Untitled') : (item.title || 'Untitled Project'), 100);
    // Country/location
    const rawLoc = item.location;
    const country = typeof rawLoc === 'object' && rawLoc !== null ? (rawLoc.name || rawLoc.label || rawLoc.id || '') : (rawLoc || '');
    // Tag meta
    const issueLabel = isPost ? getIssueLabel(item.issue) : null;
    let solutionLabel = null;
    if (!isPost) {
      const st = getSolutionTypeValue(item);
      const meta = getSolutionTypeMeta(st);
      solutionLabel = meta?.label || '';
    }

    if (isPost) {
      const excerpt = truncate(item.content || '', 180);
      return (
        <div
          key={item.id}
          className="trending-card is-post relative"
        >
          <button
            type="button"
            className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
            onClick={open}
            aria-label={`Post: ${title}`}
          />
          <div className="relative z-0 pointer-events-none">
            <div className="trend-title" title={title}>{title}</div>
          <div className="trend-meta">
            {country && <span className="trend-country">{country}</span>}
            {(country && issueLabel) && <span className="trend-sep">•</span>}
            {issueLabel && <span className="trend-tag">{issueLabel}</span>}
          </div>
            {excerpt && <div className="trend-body" title={excerpt}>{excerpt}</div>}
          </div>
        </div>
      );
    }

    const thumb = item.thumbUrl || '';
    return (
      <div
        key={item.id}
        className="trending-card is-project relative"
      >
        <button
          type="button"
          className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
          onClick={open}
          aria-label={`Project: ${title}`}
        />
        <div className="relative z-0 pointer-events-none">
          <div className="trend-title" title={title}>{title}</div>
        <div className="trend-meta">
          {country && <span className="trend-country">{country}</span>}
          {(country && solutionLabel) && <span className="trend-sep">•</span>}
          {solutionLabel && <span className="trend-tag">{solutionLabel}</span>}
        </div>
          {thumb && (
            <img className="trend-image" src={thumb} alt={title} loading="lazy" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="trending-panel">

      {isLoading && (
        <div className="space-y-6" aria-hidden>
          <div className="trending-section">
            <h4 className="trending-section-title">Trending Posts</h4>
            <div className="space-y-4">
              {[0, 1, 2].map(i => (
                <div key={`post-skel-${i}`} className="p-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 space-y-2">
                  <Skeleton height={14} width="80%" />
                  <Skeleton height={12} width="40%" />
                  <Skeleton height={40} width="100%" className="mt-2" />
                </div>
              ))}
            </div>
          </div>
          <div className="trending-section">
            <h4 className="trending-section-title">Trending Projects</h4>
            <div className="space-y-4">
              {[0, 1, 2].map(i => (
                <div key={`proj-skel-${i}`} className="p-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 space-y-2">
                  <Skeleton height={14} width="70%" />
                  <Skeleton height={12} width="30%" />
                  <Skeleton height={80} width="100%" className="mt-2 rounded-lg" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isError && !isLoading && (
        <div className="trending-error">{String(error?.message || error) || 'Failed to load trending'}</div>
      )}

      {!isLoading && !isError && (
        <div className="trending-sections">
          <div className="trending-section">
            <h4 className="trending-section-title">Trending Posts</h4>
            {posts.length === 0 ? (
              <div className="trending-empty">No popular posts yet.</div>
            ) : (
              <div className="trending-cards">
                {posts.map(p => renderCard(p, 'post'))}
              </div>
            )}
          </div>

          <div className="trending-section">
            <h4 className="trending-section-title">Trending Projects</h4>
            {projects.length === 0 ? (
              <div className="trending-empty">No projects yet</div>
            ) : (
              <div className="trending-cards">
                {projects.map(pr => renderCard(pr, 'project'))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
